import { useEffect, useState } from "react";

export const CART_GLOBAL_VARIABLE_NAME = "BouncieEmbeddedCart" as const;

// The embed <script> loads asynchronously, so `window.BouncieEmbeddedCart` may not exist
// yet when this runs. Listening on `window` directly (rather than the object's own
// `events.ready.subscribe()`) works regardless — see checkoutdwa's embed/README.md.
// Only `ready` needs this: once it's fired, the object is guaranteed to exist, so
// everything else (e.g. `pricingLoaded`) can safely use the standard events API.
const CART_READY_EVENT = `${CART_GLOBAL_VARIABLE_NAME}_ready`;

// Which value is picked for each variant attribute (e.g. { "Connector Type": "J1772" }).
// Part of a cart line's identity, not just display — two entries with the same
// productCode but different selection are different lines (checkoutdwa's buildItemKey).
export type ProductSelection = Record<string, string>;

export interface CartProduct {
  productCode: string;
  quantity: number;
  selection?: ProductSelection;
}

// A variant attribute a product can be picked by (e.g. "Connector Type"), and the values
// it can take (e.g. "J1772"/"NACS") — see checkoutdwa's buildPricingCatalog.js. Not every
// product has these; single-variant products (e.g. bouncieobd) omit the field entirely.
export interface CartProductAttribute {
  key: string;
  values: { key: string }[];
}

// currency is a lowercase 3-letter code ("usd"/"cad") — see checkoutdwa's buildPricingCatalog.js.
export interface CartPricingProductVariant {
  currency: string;
  currencySymbol: string;
  unitPrice: number;
}

// `products` is no longer per-currency — pricing is nested one level down in
// `variants` (one entry per currency) instead of flat fields on the product itself.
export interface CartPricingProduct {
  productCode: string;
  attributes?: CartProductAttribute[];
  variants?: CartPricingProductVariant[];
}

export interface CartPricingSubscription {
  country: string;
  currency: string;
  currencySymbol: string;
  baseUnitPrice: number;
  adjustedUnitPrice: number;
  discountThreshold: number;
}

export interface PricingCatalog {
  products: CartPricingProduct[];
  subscriptions: CartPricingSubscription[];
}

// Partial — only the surface these hooks touch. See checkoutdwa's embed/README.md.
export interface BouncieEmbeddedCart {
  isReady?: boolean;
  pricingCatalog?: PricingCatalog;
  events?: {
    toggleCart?: { emit: () => void };
    pricingLoaded?: { subscribe: (handler: () => void) => () => void };
  };
  state?: {
    getPersist: () => { isCartPanelOpen?: boolean; products?: CartProduct[]; currency?: string; language?: string };
    updatePersist: (update: Record<string, unknown>) => void;
    subscribe: (handler: () => void) => () => void;
    utils: {
      getProductQuantity: (productCode: string, selection: ProductSelection, products?: CartProduct[]) => number;
      // A quantity <= 0 removes the line entirely — see checkoutdwa's setProductQuantity.
      setProductQuantity: (productCode: string, selection: ProductSelection, quantity: number, products?: CartProduct[]) => CartProduct[];
    };
  };
}

declare global {
  interface Window {
    BouncieEmbeddedCart?: BouncieEmbeddedCart;
  }
}

export function getCart(): BouncieEmbeddedCart | undefined {
  if (typeof window === "undefined") return undefined;
  return window[CART_GLOBAL_VARIABLE_NAME];
}

// Mirrors bouncieweb's useCartReady.
export function useEmbeddedCartReady(): boolean {
  const [isReady, setIsReady] = useState(() => Boolean(getCart()?.isReady));

  useEffect(() => {
    if (isReady) return;

    const onReady = () => setIsReady(true);
    window.addEventListener(CART_READY_EVENT, onReady);

    // Handle the case where the cart became ready before this listener was attached.
    // Deferred via queueMicrotask, not called synchronously — the effect body itself
    // shouldn't trigger a same-commit cascading render.
    if (getCart()?.isReady) {
      queueMicrotask(onReady);
    }

    return () => window.removeEventListener(CART_READY_EVENT, onReady);
  }, [isReady]);

  return isReady;
}

// Shared by useCurrencyPreference/useLocalePreference: keeps a preference in sync with the
// embedded cart's shared cookie whenever checkoutdwa (or another Bouncie property) changes
// it. The 'focus' listener is what catches a full-page round trip to checkoutdwa and back —
// a plain cookie write from another page doesn't otherwise notify this page. 'pageshow' covers
// the bfcache-restore case 'focus' misses (Back navigation doesn't reliably re-fire focus).
// Written once here and called from within each preference hook, rather than as its own hook
// to mount, so callers get sync "for free" just by using the preference hook they already need.
export function useSyncedCartField<T extends string>([value, setValue]: [T, (next: T) => void], getCartValue: () => T | undefined): void {
  const isCartReady = useEmbeddedCartReady();

  useEffect(() => {
    if (!isCartReady) return;

    const syncFromCart = () => {
      const cartValue = getCartValue();
      if (cartValue && cartValue !== value) {
        setValue(cartValue);
      }
    };

    const unsubscribe = getCart()?.state?.subscribe(syncFromCart);
    window.addEventListener("focus", syncFromCart);
    window.addEventListener("pageshow", syncFromCart);
    syncFromCart();

    return () => {
      unsubscribe?.();
      window.removeEventListener("focus", syncFromCart);
      window.removeEventListener("pageshow", syncFromCart);
    };
  }, [isCartReady, value, setValue, getCartValue]);
}
